import React from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock } from 'lucide-react';

export interface DaySchedule {
  enabled: boolean;
  start: string;
  end: string;
}

export interface WeeklySchedule {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
}

const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
] as const;

const TIME_OPTIONS = [
  '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30',
  '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
  '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
  '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00'
];

const formatTime = (time: string) => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

export const getDefaultSchedule = (): WeeklySchedule => ({
  monday: { enabled: false, start: '09:00', end: '17:00' },
  tuesday: { enabled: false, start: '09:00', end: '17:00' },
  wednesday: { enabled: false, start: '09:00', end: '17:00' },
  thursday: { enabled: false, start: '09:00', end: '17:00' },
  friday: { enabled: false, start: '09:00', end: '17:00' },
  saturday: { enabled: false, start: '10:00', end: '16:00' },
  sunday: { enabled: false, start: '10:00', end: '16:00' },
});

interface AvailabilityScheduleProps {
  schedule: WeeklySchedule;
  onChange: (schedule: WeeklySchedule) => void;
  readOnly?: boolean;
}

export const AvailabilitySchedule: React.FC<AvailabilityScheduleProps> = ({
  schedule,
  onChange,
  readOnly = false,
}) => {
  const handleToggleDay = (day: keyof WeeklySchedule) => {
    if (readOnly) return;
    onChange({
      ...schedule,
      [day]: { ...schedule[day], enabled: !schedule[day].enabled },
    });
  };

  const handleTimeChange = (day: keyof WeeklySchedule, field: 'start' | 'end', value: string) => {
    if (readOnly) return;
    onChange({
      ...schedule,
      [day]: { ...schedule[day], [field]: value },
    });
  };

  return (
    <div className="space-y-3">
      {DAYS.map(({ key, label }) => (
        <div
          key={key}
          className={`flex items-center gap-4 p-3 rounded-lg border transition-colors ${
            schedule[key].enabled
              ? 'bg-primary/5 border-primary/20'
              : 'bg-muted/30 border-border'
          }`}
        >
          <div className="flex items-center gap-3 min-w-[140px]">
            {!readOnly && (
              <Switch
                checked={schedule[key].enabled}
                onCheckedChange={() => handleToggleDay(key)}
                aria-label={`Toggle ${label}`}
              />
            )}
            <Label className={`font-medium ${schedule[key].enabled ? 'text-foreground' : 'text-muted-foreground'}`}>
              {label}
            </Label>
          </div>

          {schedule[key].enabled ? (
            <div className="flex items-center gap-2 flex-1">
              {readOnly ? (
                <span className="text-sm text-foreground">
                  {formatTime(schedule[key].start)} - {formatTime(schedule[key].end)}
                </span>
              ) : (
                <>
                  <Select
                    value={schedule[key].start}
                    onValueChange={(value) => handleTimeChange(key, 'start', value)}
                  >
                    <SelectTrigger className="w-[110px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.map((time) => (
                        <SelectItem key={time} value={time}>
                          {formatTime(time)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-muted-foreground">to</span>
                  <Select
                    value={schedule[key].end}
                    onValueChange={(value) => handleTimeChange(key, 'end', value)}
                  >
                    <SelectTrigger className="w-[110px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.filter((time) => time > schedule[key].start).map((time) => (
                        <SelectItem key={time} value={time}>
                          {formatTime(time)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>
          ) : (
            <span className="text-sm text-muted-foreground italic">
              {readOnly ? 'Unavailable' : 'Not available'}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

// Compact display component for service listings
interface AvailabilityDisplayProps {
  schedule: WeeklySchedule;
}

export const AvailabilityDisplay: React.FC<AvailabilityDisplayProps> = ({ schedule }) => {
  const enabledDays = DAYS.filter(({ key }) => schedule[key].enabled);

  if (enabledDays.length === 0) {
    return null;
  }

  return (
    <div className="flex items-start gap-2 text-sm">
      <Clock className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="space-y-0.5">
        {enabledDays.map(({ key, label }) => (
          <div key={key} className="text-muted-foreground">
            <span className="font-medium text-foreground">{label.slice(0, 3)}</span>
            {' '}
            {formatTime(schedule[key].start)} - {formatTime(schedule[key].end)}
          </div>
        ))}
      </div>
    </div>
  );
};
